import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useSupabaseClient } from '~/composables/useSupabaseClient'
import { useToast } from '~/composables/useToast'
import {
  BACKOFFICE_SESSION_REFRESH_INTERVAL_MS,
  BACKOFFICE_SESSION_TIMEOUT_LABEL_FR,
  BACKOFFICE_SESSION_TIMEOUT_MS,
  BACKOFFICE_SESSION_WARNING_MS
} from '~/utils/backofficeSession'

export const useSessionManagement = () => {
  const supabase = useSupabaseClient()
  const { showToast } = useToast()
  
  // Session configuration
  const SESSION_TIMEOUT = BACKOFFICE_SESSION_TIMEOUT_MS
  const WARNING_TIME = BACKOFFICE_SESSION_WARNING_MS
  const REFRESH_INTERVAL = BACKOFFICE_SESSION_REFRESH_INTERVAL_MS
  
  // State
  const isSessionActive = ref(true)
  const sessionWarningShown = ref(false)
  const lastActivity = ref(Date.now())
  
  // Timers
  let sessionTimer: NodeJS.Timeout | null = null
  let warningTimer: NodeJS.Timeout | null = null
  let refreshTimer: NodeJS.Timeout | null = null
  let activityTimer: NodeJS.Timeout | null = null
  
  // Update last activity
  const updateActivity = () => {
    lastActivity.value = Date.now()
    sessionWarningShown.value = false
    
    // Reset timers
    resetTimers()
    startTimers()
  }
  
  // Reset all timers
  const resetTimers = () => {
    if (sessionTimer) clearTimeout(sessionTimer)
    if (warningTimer) clearTimeout(warningTimer)
    if (refreshTimer) clearInterval(refreshTimer)
    if (activityTimer) clearTimeout(activityTimer)
  }
  
  // Start all timers
  const startTimers = () => {
    // Session timeout timer
    sessionTimer = setTimeout(() => {
      
      logout()
    }, SESSION_TIMEOUT)
    
    // Warning timer
    warningTimer = setTimeout(() => {
      
      showSessionWarning()
    }, SESSION_TIMEOUT - WARNING_TIME)
    
    // Auto refresh timer
    refreshTimer = setInterval(() => {
      
      refreshSession()
    }, REFRESH_INTERVAL)
  }
  
  // Show session warning
  const showSessionWarning = () => {
    if (sessionWarningShown.value) return
    
    sessionWarningShown.value = true
    showToast({
      type: 'warning',
      title: 'Session expirera bientôt',
      message: 'Votre session expirera dans 5 minutes. Cliquez pour continuer.',
      duration: 0 // No auto-dismiss
    })
    
    // Add click listener to extend session
    const handleClick = () => {
      updateActivity()
      showToast({
        type: 'success',
        title: 'Session prolongée',
        message: `Votre session a été prolongée de ${BACKOFFICE_SESSION_TIMEOUT_LABEL_FR}.`
      })
    }
    
    // Listen for any user activity
    document.addEventListener('click', handleClick, { once: true })
    document.addEventListener('keydown', handleClick, { once: true })
  }
  
  // Refresh session
  const refreshSession = async () => {
    try {
      const { data: { user }, error } = await supabase.auth.getUser()
      
      if (error || !user) {

        logout()
        return
      }
      
      // Update last login in database
      await $fetch('/api/update-last-login', {
        method: 'POST',
        headers: await getBearerHeaders(),
        body: { email: user.email }
      })
      
      
    } catch (error) {
      console.error('[SESSION] Failed to refresh session:', error)
      logout()
    }
  }
  
  // Logout
  const logout = async () => {
    try {
      
      
      // Clear all timers
      resetTimers()
      
      // Sign out from Supabase
      await supabase.auth.signOut()
      
      // Show logout message
      showToast({
        type: 'info',
        title: 'Déconnexion',
        message: 'Vous avez été déconnecté pour inactivité.'
      })
      
      // Redirect to login
      await navigateTo('/app/login')
    } catch (error) {
      console.error('[SESSION] Error during logout:', error)
    }
  }
  
  // Manual logout
  const manualLogout = async () => {
    try {
      
      
      // Clear all timers
      resetTimers()
      
      // Sign out from Supabase
      await supabase.auth.signOut()
      
      // Show logout message
      showToast({
        type: 'success',
        title: 'Déconnexion',
        message: 'Vous avez été déconnecté avec succès.'
      })
      
      // Redirect to login
      await navigateTo('/app/login')
    } catch (error) {
      console.error('[SESSION] Error during manual logout:', error)
    }
  }
  
  // Initialize session management
  const initSessionManagement = () => {
    
    
    // Start timers
    startTimers()
    
    // Set up activity listeners
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']
    
    events.forEach((event) => {
      document.addEventListener(event, updateActivity, { passive: true })
    })
    
    // Debounced activity update
    const debouncedUpdate = () => {
      if (activityTimer) clearTimeout(activityTimer)
      activityTimer = setTimeout(updateActivity, 1000) // Debounce to 1 second
    }
    
    // Add debounced listeners for performance
    document.addEventListener('mousemove', debouncedUpdate, { passive: true })
    document.addEventListener('scroll', debouncedUpdate, { passive: true })
  }
  
  // Cleanup
  const cleanup = () => {
    
    resetTimers()
    
    // Remove event listeners
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']
    events.forEach((event) => {
      document.removeEventListener(event, updateActivity)
    })
  }
  
  // Lifecycle hooks
  onMounted(async () => {
    // Wait for router to be ready
    await nextTick()
    initSessionManagement()
  })
  
  onBeforeUnmount(() => {
    cleanup()
  })
  
  return {
    isSessionActive,
    sessionWarningShown,
    lastActivity,
    updateActivity,
    manualLogout,
    refreshSession
  }
} 
